import { COLORS } from './format';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { InboundRequest } from '../../types/taActionBoard';

/**
 * The booking situation behind a scheduling request, and the four things a recruiter can do about
 * it without leaving the tab.
 *
 * Green when the draft already carries a booking link that was checked to have slots; amber when it
 * does not, naming WHY — most usefully "Lena has no free slot in the window", which is the diagnosis
 * that otherwise costs a recruiter a trip through two calendars. Renders nothing for non-scheduling
 * intents.
 *
 * ── The actions, and why they are all offered at once (Sofia, Aug 13 2026) ───────────────────
 * They used to appear only for the thing the candidate asked about, which meant a reschedule request
 * offered no way to cancel and a cancellation offered no way to fix the link. What the candidate
 * ASKED for is not the same as what the recruiter needs to DO — a reschedule often ends in a
 * withdrawal, and a cancellation is often really "this interviewer can't make it".
 *
 *  🗑 Cancel their interview — FINN-side, attributed, one notice (needs a live booking).
 *  🕑 Let them reschedule   — the candidate moves it themselves: their own link goes in the reply.
 *                             The standing rule, and the first thing to reach for.
 *  📆 Reschedule for them   — FINN moves it, for when that rule cannot work: they named a time by
 *                             email, or the interviewer dropped out.
 *  ⚙️ Edit the link         — the interview link's own editor, in a modal, because "widen the window
 *                             or swap the interviewer" is the advice these messages keep giving.
 */
export function SchedulingBox({
  scheduling,
  onCancelInterview,
  cancelState,
  onEditLink,
  onRescheduleHint,
  rescheduleHinted,
  onRescheduleForThem,
}: {
  scheduling: InboundRequest['scheduling'];
  /**
   * Queues the FINN-side cancel behind the board's 10-second countdown. Present only where the
   * tab can act (a live booking); the box stays purely informational otherwise.
   */
  onCancelInterview?: () => void;
  /** What happened to a queued cancel, so the button tells the truth afterwards. */
  cancelState?: 'idle' | 'pending' | 'cancelled' | 'already_cancelled';
  /** Opens the interview link's editor in a modal, without leaving the tab. */
  onEditLink?: () => void;
  /**
   * Puts the candidate's own reschedule link into the reply. Not a FINN-side move: the candidate
   * picks the time, which is the standing rule for reschedules.
   */
  onRescheduleHint?: () => void;
  /** True once the link + wording were added, so the button stops offering it again. */
  rescheduleHinted?: boolean;
  /** Opens the FINN-side move: pick a free slot, or set a time agreed by email. */
  onRescheduleForThem?: () => void;
}) {
  if (!scheduling) return null;
  const ok = scheduling.linkOffered;
  // A checked link the recruiter still has to place counts as good news too.
  const good = ok || Boolean(scheduling.bookingUrl);
  const cancelDone = cancelState === 'cancelled' || cancelState === 'already_cancelled';
  /** Anything to act on at all: a live booking, or a link worth editing. */
  const hasActions = Boolean(
    (onCancelInterview && scheduling.bookingRef) ||
      (onRescheduleHint && scheduling.bookingUrl) ||
      (onRescheduleForThem && scheduling.bookingRef) ||
      (onEditLink && scheduling.linkId)
  );

  return (
    <div
      className="rounded-lg p-3 text-sm"
      style={
        good
          ? { backgroundColor: COLORS.greenBg, color: COLORS.greenText }
          : { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }
      }
    >
      <span className="font-semibold">
        {ok ? 'Booking link included. ' : good ? 'Booking link ready. ' : 'No booking link. '}
      </span>
      {scheduling.message}

      {cancelDone && (
        <p className="mt-2 text-xs font-medium">
          {cancelState === 'cancelled'
            ? 'Interview cancelled — send them a quick confirmation.'
            : 'Already cancelled (by the candidate or a colleague).'}
        </p>
      )}

      {hasActions && (
        <div className="mt-2 flex flex-wrap gap-2" data-no-toggle>
          {/*
            Offered whatever the candidate asked for — a reschedule request very often ends as a
            cancellation. Hidden only once it IS cancelled, where it would be a lie.
          */}
          {onCancelInterview && scheduling.bookingRef && !cancelDone && (
            <Button
              size="sm"
              variant="outline"
              data-no-toggle
              disabled={cancelState === 'pending'}
              onClick={onCancelInterview}
            >
              🗑 Cancel their interview
            </Button>
          )}
          {/*
            Reschedule = hand the candidate their own link. Pointless once cancelled: there would be
            nothing left to move.
          */}
          {onRescheduleHint && scheduling.bookingUrl && !cancelDone && (
            <Button
              size="sm"
              variant="outline"
              data-no-toggle
              // Also disabled while a cancel is counting down: offering the candidate a reschedule
              // link for an interview that is about to be cancelled is a contradiction in flight.
              disabled={rescheduleHinted || cancelState === 'pending'}
              onClick={onRescheduleHint}
            >
              {rescheduleHinted ? 'Reschedule link in the reply' : 'Let them reschedule'}
            </Button>
          )}
          {/*
            FINN takes the decision instead of handing it over — needs a live booking, and is
            pointless once cancelled. Offered beside "let them reschedule", never instead of it: the
            standing rule is still that the candidate picks when they can.
          */}
          {onRescheduleForThem && scheduling.bookingRef && !cancelDone && (
            <Button
              size="sm"
              variant="outline"
              data-no-toggle
              disabled={cancelState === 'pending'}
              onClick={onRescheduleForThem}
            >
              📆 Reschedule for them
            </Button>
          )}
          {onEditLink && scheduling.linkId && (
            <Button size="sm" variant="ghost" data-no-toggle onClick={onEditLink}>
              ⚙️ Edit the link
            </Button>
          )}
        </div>
      )}

      {/* Shown when the draft does NOT carry it, so the recruiter can copy it in. */}
      {!ok && scheduling.bookingUrl ? (
        <code className="mt-1.5 block break-all rounded bg-white/60 p-1.5 text-xs">
          {scheduling.bookingUrl}
        </code>
      ) : null}
    </div>
  );
}
